import { makeStyles, Typography } from "@material-ui/core";
import ActionDialogVariant from "@saleor/components/ActionDialogVariant";
import Container from "@saleor/components/Container";
import { DEFAULT_INITIAL_SEARCH_DATA } from "@saleor/config";
import CouponFullDetails, {
  CouponFullDetailsPageFormData
} from "@saleor/coupons/components/CouponDetailsPage";
import {
  useActivateCoupon,
  useAssignCouponToCollection,
  useCouponCreateDeepLink,
  useCouponDeactivate,
  useCouponUpdate,
  useDraftCouponUpdate,
  useRemoveCouponToCollection
} from "@saleor/coupons/mutations";
import {
  useCouponCollectionDetailQuery,
  useCouponFullDetailsQuery
} from "@saleor/coupons/queries";
import { CouponActivate } from "@saleor/coupons/types/CouponActivate";
import { CouponDeactivate } from "@saleor/coupons/types/CouponDeactivate";
import { DraftCouponUpdate } from "@saleor/coupons/types/DraftCouponUpdate";
import {
  couponListPath,
  couponManageUrl,
  couponUrl
} from "@saleor/coupons/url";
import {
  canManageCouponUsageLimitByDealType,
  resolveCouponUsageLimitForUpdate
} from "@saleor/coupons/utils";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import useUser from "@saleor/hooks/useUser";
import { findValueInEnum, maybe } from "@saleor/misc";
import useCouponCollectionSearch from "@saleor/searches/useCouponCollectionSearch";
import useMallSearch from "@saleor/searches/useMallSearch";
import {
  CouponAssigneeEnum,
  CouponCategoryEnum,
  CouponCollectionCategoryEnum,
  CouponCollectionSortField,
  CouponCollectionStatusEnum,
  CouponDealTypeEnum,
  CouponOccurenceEnum,
  CouponOccurenceExcludeEnum,
  CouponRedeemTypeEnum,
  CouponStatusEnum,
  CouponUserTypeEnum,
  MallSortField,
  OrderBehavior,
  OrderDirection,
  SmopStatusEnum
} from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import { changeTimeZone } from "@saleor/utils/smop/changeTimeZoneDateTime";
import {
  createCouponValidation,
  hasNoError
} from "@saleor/utils/validators/coupon";
import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

export interface CouponDetailsViewProps {
  id: string;
  params: any;
}

const useStyles = makeStyles(
  () => ({
    subContent: {
      color: "#747C8C",
      fontSize: "14px",
      padding: "0 1em",
      textAlign: "center"
    }
  }),
  { name: "CouponDetailsPage" }
);

const CouponDetails: React.FC<CouponDetailsViewProps> = ({ id, params }) => {
  const intl = useIntl();
  const notify = useNotifier();
  const navigate = useNavigator();
  const [promoImageUpload, setPromoImageUpload] = useState(null);
  const [accessImageUpload, setAccessImageUpload] = useState(null);
  const [additionalCopies, setAdditionalCopies] = useState(0);

  const {
    loadMore: loadMoreMalls,
    search: searchMall,
    result: searchMallOpts
  } = useMallSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      filterStatus: SmopStatusEnum.ACT,
      first: 100,
      sortBy: {
        behavior: OrderBehavior.CASE_INSENSITIVE,
        direction: OrderDirection.ASC,
        field: MallSortField.NAME
      },
      userMalls: true
    }
  });
  const mallTags = maybe(() => searchMallOpts.data.search.edges, []).map(
    edge => edge.node
  );

  const { data, loading, refetch } = useCouponFullDetailsQuery({
    displayLoader: true,
    variables: {
      id
    }
  });
  const couponFullDetails = data?.coupon;
  const currentCollection = couponFullDetails?.collection?.id;
  const couponId = couponFullDetails?.id;

  const { data: currentCollectionDetails } = useCouponCollectionDetailQuery({
    skip: currentCollection === undefined,
    variables: {
      id: currentCollection
    }
  });
  const [selectedCategory, setSelectedCategory] = useState([
    data?.coupon?.category?.toString() as CouponCollectionCategoryEnum
  ]);
  const [selectedCollection, setSelectedCollection] = useState(
    data?.coupon?.collection?.id
  );

  const {
    loadMore: loadMoreCollection,
    search: searchCollection,
    result: searchCollectionOpts
  } = useCouponCollectionSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      category: selectedCategory,
      sortBy: {
        direction: OrderDirection.DESC,
        field: CouponCollectionSortField.UPDATED_AT
      },
      status: [
        CouponCollectionStatusEnum.ACTIVE,
        CouponCollectionStatusEnum.INACTIVE,
        CouponCollectionStatusEnum.SCHEDULED,
        CouponCollectionStatusEnum.EXPIRED
      ]
    }
  });

  const [removeCouponToCollection] = useRemoveCouponToCollection({
    onCompleted: data => {
      if (data.setCouponsToCollection.errors.length !== 0) {
        notify({
          status: "error",
          text: data.setCouponsToCollection.errors[0].message
        });
      }
    }
  });

  const [assignCouponToCollection] = useAssignCouponToCollection({
    onCompleted: data => {
      if (data.setCouponsToCollection.errors.length !== 0) {
        notify({
          status: "error",
          text: data.setCouponsToCollection.errors[0].message
        });
      }
    }
  });

  const handleSetCouponToCollection = async (
    desiredCollectionId?: string | null
  ) => {
    const desired =
      desiredCollectionId !== undefined
        ? desiredCollectionId
        : selectedCollection;

    const hasDesired = desired != null && String(desired).trim() !== "";

    if (currentCollection != null && currentCollection !== "") {
      if (!hasDesired) {
        await removeCouponToCollection({
          variables: {
            coupons: { remove: [couponId] },
            instanceId: currentCollection
          }
        });
      } else if (currentCollection !== desired) {
        await assignCouponToCollection({
          variables: {
            couponId,
            instanceId: desired
          }
        });
      }
    } else if (hasDesired) {
      await assignCouponToCollection({
        variables: {
          couponId,
          instanceId: desired
        }
      });
    }
  };

  const [createDeeplink, createDeeplinkOpts] = useCouponCreateDeepLink({
    onCompleted: data => {
      const errors = data?.couponGenerateDynamicLink?.discountErrors;
      if (errors?.length === 0) {
        refetch();
      } else {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage: "Oops, something went wrong...",
            description: "snackbar text"
          })
        });
      }
    }
  });
  const { user } = useUser();

  const userPermissions = user?.userPermissions || [];

  const [openModal, closeModal] = createDialogActionHandlers(
    navigate,
    params => couponUrl(id, params),
    params
  );

  const handleDeactivateSuccess = async (data: CouponDeactivate) => {
    if (data?.couponDeactivate?.discountErrors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Coupon Successfully Deactivated"
        })
      });
      closeModal();
      await refetch();
    } else if (
      data?.couponDeactivate?.discountErrors.some(e => e.code === "INVALID")
    ) {
      notify({
        status: "error",
        text: data.couponDeactivate?.discountErrors[0].message
      });
      closeModal();
    }
  };

  const [deactivateCoupon, deactivateCouponResult] = useCouponDeactivate({
    onCompleted: handleDeactivateSuccess
  });

  const handleCouponDeactivate = id => {
    deactivateCoupon({
      variables: {
        couponId: id
      }
    });
  };

  const handleActivateSuccess = async (data: CouponActivate) => {
    if (data?.couponActivate?.discountErrors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Coupon Activated Successfully"
        })
      });
      await refetch();
    } else if (
      data?.couponActivate?.discountErrors.some(e => e.code === "INVALID")
    ) {
      notify({
        status: "error",
        text: data.couponActivate?.discountErrors[0].message
      });
      closeModal();
    }
  };

  const [activateCoupon] = useActivateCoupon({
    onCompleted: handleActivateSuccess
  });

  const handleCouponActivate = id => {
    activateCoupon({
      variables: {
        couponId: id
      }
    });
  };

  const handleGenerateDeeplink = (id: string) => {
    if (
      couponFullDetails &&
      couponFullDetails.status !== CouponStatusEnum.DRAFT
    ) {
      createDeeplink({ variables: { id } });
    }
  };

  const classes = useStyles({});

  const handleCouponDraftUpdateSuccess = async (data: DraftCouponUpdate) => {
    if (data?.draftCouponUpdate?.errors.length === 0) {
      if (selectedCollection !== null) {
        await handleSetCouponToCollection();
      }
      notify({
        status: "success",
        text: intl.formatMessage({ defaultMessage: "Coupon Draft updated" })
      });
      setTimeout(() => {
        navigate(couponManageUrl);
      }, 1000);
      await refetch();
    } else if (
      data?.draftCouponUpdate?.errors.some(e => e.code === "INVALID")
    ) {
      notify({
        status: "error",
        text: data?.draftCouponUpdate?.errors[0].message
      });
    }
  };

  const formDataValidations = formData => {
    const alphanumericRegex = /^[a-zA-Z0-9]+$/;
    const couponValueRegex = /^\d+(\.\d{1,2})?$/;

    if (formData.couponCode && !alphanumericRegex.test(formData.couponCode)) {
      notify({
        status: "error",
        text: "Coupon Code must contain only alphanumeric characters",
        title: "Coupon Form Error"
      });
      return false;
    }
    if (
      typeof formData.couponValue === "number" ||
      (typeof formData.couponValue === "string" &&
        formData.couponValue.trim() !== "")
    ) {
      if (parseFloat(formData.couponValue) <= 0) {
        notify({
          status: "error",
          text: "Discounted Price must be greater than 0",
          title: "Can't Submit"
        });
        return false;
      }
      if (!couponValueRegex.test(formData.couponValue)) {
        notify({
          status: "error",
          text: "Discounted Price can only accept two decimal places.",
          title: "Can't Submit"
        });
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (
    formData: CouponFullDetailsPageFormData
  ): Promise<Array<{ code?: string; message?: string }>> => {
    const validateForm = createCouponValidation(formData, {
      canManageCouponUsageLimit: canManageCouponUsageLimitByDealType(
        userPermissions,
        formData.couponClassification || couponFullDetails?.dealType
      )
    });
    if (!formDataValidations(formData) || !hasNoError(validateForm)) {
      return [];
    }
    setSelectedCollection(formData.couponCollection);
    const couponUpdateInput = {
      ...(accessImageUpload && {
        accessImage: formData.accessImage,
        accessImageAlt: formData.accessImageAlt
      }),
      accessPageDescription: JSON.stringify(formData.accessPageDescription),
      accessPageSubtitle: formData.subtitle,
      accessPageTagline: formData.tagline,
      assignee: maybe(() =>
        findValueInEnum(formData.assignee, CouponAssigneeEnum)
      ),
      category: maybe(() =>
        findValueInEnum(formData.couponClassification, CouponDealTypeEnum) ===
        CouponDealTypeEnum.DEALS
          ? findValueInEnum(formData.category, CouponCategoryEnum)
          : null
      ),
      code: formData.couponCode,
      dealType: maybe(() =>
        findValueInEnum(formData.couponClassification, CouponDealTypeEnum)
      ),
      discountValue: formData.couponValue,
      endDate: changeTimeZone(formData.couponEndDate, formData.couponEndTime),
      eventEndDate: changeTimeZone(
        formData.eventStartDate,
        formData.eventStartTime
      ),
      eventLocation: formData.eventLocation,
      eventStartDate: changeTimeZone(
        formData.eventStartDate,
        formData.eventStartTime
      ),
      hasMalls: formData.hasMalls,
      isAppExclusive: formData.isAppExclusive,
      isCodeUnlocked: formData.isCodeUnlocked,
      isExclusiveRedemption: formData.isExclusiveRedemption,
      isFeatured: formData.isFeatured,
      isHidden: formData.isHidden,
      isNationwide: formData.isNationwide,
      isUsageTimerApplied: formData.isUsageTimerApplied,
      mainPromoDescription: JSON.stringify(formData.mainPromoDescription),
      mainPromoTitle: formData.mainPromoTitle,
      malls: formData.malls,
      mechanics: JSON.stringify(formData.mechanics),
      name: formData.mainPromoTitle,
      occurence: maybe(() =>
        findValueInEnum(formData.occurrence, CouponOccurenceEnum)
      ),
      occurenceExclude: formData.excludedDays.map(day =>
        maybe(() => findValueInEnum(day, CouponOccurenceExcludeEnum))
      ),
      ...(promoImageUpload && {
        promoImage: formData.promoImage,
        promoImageAlt: formData.promoImageAlt
      }),
      quantity:
        couponFullDetails?.status === "DRAFT"
          ? formData.totalCopies || null
          : formData.addedCopies,
      redeemEndDate: changeTimeZone(
        formData.redeemingEndDate,
        formData.redeemingEndTime
      ),
      redeemStartDate: changeTimeZone(
        formData.redeemingStartDate,
        formData.redeemingStartTime
      ),
      redeemType: maybe(() =>
        findValueInEnum(formData.redeemType, CouponRedeemTypeEnum)
      ),
      redemptionExclude: formData.excludedDays.map(day =>
        maybe(() => findValueInEnum(day, CouponOccurenceExcludeEnum))
      ),
      redemptionFrequency: maybe(() =>
        findValueInEnum(formData.occurrence, CouponOccurenceEnum)
      ),
      startDate: changeTimeZone(
        formData.couponStartDate,
        formData.couponStartTime
      ),
      status:
        couponFullDetails.status === CouponStatusEnum.INACTIVE
          ? CouponStatusEnum.INACTIVE
          : CouponStatusEnum.ACTIVE,
      usageLimit: resolveCouponUsageLimitForUpdate(
        canManageCouponUsageLimitByDealType(
          userPermissions,
          formData.couponClassification || couponFullDetails?.dealType
        ),
        formData.usageLimit,
        couponFullDetails?.usageLimit
      ),
      usageTimerHour: formData.usageTimerHour,
      usageTimerMinute: formData.usageTimerMinute,
      usageTimerSecond: formData.usageTimerSecond,
      userType: maybe(() =>
        findValueInEnum(formData.userRestriction, CouponUserTypeEnum)
      )
    };

    if (currentCollection !== formData.couponCollection && currentCollection) {
      await removeCouponToCollection({
        variables: {
          coupons: { remove: [couponId] },
          instanceId: currentCollection
        }
      });
    }

    const result = await updateCoupon({
      variables: {
        first: 100,
        id,
        input: couponUpdateInput
      }
    });
    setAdditionalCopies(0);
    const errors = result?.data?.couponUpdate?.errors ?? [];
    if (errors.length === 0) {
      await handleSetCouponToCollection(formData.couponCollection);
      let successMessage = intl.formatMessage({
        defaultMessage: "Coupon updated"
      });
      if (couponFullDetails.status === "DRAFT") {
        successMessage = intl.formatMessage({
          defaultMessage: "Coupon created Successfully"
        });
      }
      notify({
        status: "success",
        text: successMessage
      });
      await refetch();
    } else if (errors.some(e => e.code === "INVALID")) {
      notify({
        status: "error",
        text: errors[0].message
      });
    }
    return errors;
  };

  const handleDraftUpdate = async (formData: CouponFullDetailsPageFormData) => {
    setSelectedCollection(formData.couponCollection);
    const couponDraftUpdateInput = {
      ...(accessImageUpload && {
        accessImage: formData.accessImage,
        accessImageAlt: formData.accessImageAlt
      }),
      accessPageDescription: JSON.stringify(formData.accessPageDescription),
      accessPageSubtitle: formData.subtitle,
      accessPageTagline: formData.tagline,
      assignee: maybe(() =>
        findValueInEnum(formData.assignee, CouponAssigneeEnum)
      ),
      category: maybe(() =>
        findValueInEnum(formData.couponClassification, CouponDealTypeEnum) ===
        CouponDealTypeEnum.DEALS
          ? findValueInEnum(formData.category, CouponCategoryEnum)
          : null
      ),
      code: formData.couponCode,
      dealType: maybe(() =>
        findValueInEnum(formData.couponClassification, CouponDealTypeEnum)
      ),
      discountValue: formData.couponValue,
      endDate: changeTimeZone(formData.couponEndDate, formData.couponEndTime),
      eventEndDate: changeTimeZone(
        formData.eventStartDate,
        formData.eventStartTime
      ),
      eventLocation: formData.eventLocation,
      eventStartDate: changeTimeZone(
        formData.eventStartDate,
        formData.eventStartTime
      ),
      hasMalls: formData.hasMalls,
      isAppExclusive: formData.isAppExclusive,
      isCodeUnlocked: formData.isCodeUnlocked,
      isExclusiveRedemption: formData.isExclusiveRedemption,
      isFeatured: formData.isFeatured,
      isHidden: formData.isHidden,
      isNationwide: formData.isNationwide,
      isUsageTimerApplied: formData.isUsageTimerApplied,
      mainPromoDescription: JSON.stringify(formData.mainPromoDescription),
      mainPromoTitle: formData.mainPromoTitle,
      malls: formData.malls,
      mechanics: JSON.stringify(formData.mechanics),
      name: formData.mainPromoTitle,
      occurence: maybe(() =>
        findValueInEnum(formData.occurrence, CouponOccurenceEnum)
      ),
      occurenceExclude: formData.excludedDays.map(day =>
        maybe(() => findValueInEnum(day, CouponOccurenceExcludeEnum))
      ),
      ...(promoImageUpload && {
        promoImage: formData.promoImage,
        promoImageAlt: formData.promoImageAlt
      }),
      quantity: formData.totalCopies || null,
      redeemEndDate: changeTimeZone(
        formData.redeemingEndDate,
        formData.redeemingEndTime
      ),
      redeemStartDate: changeTimeZone(
        formData.redeemingStartDate,
        formData.redeemingStartTime
      ),
      redeemType: maybe(() =>
        findValueInEnum(formData.redeemType, CouponRedeemTypeEnum)
      ),
      redemptionExclude: formData.excludedDays.map(day =>
        maybe(() => findValueInEnum(day, CouponOccurenceExcludeEnum))
      ),
      redemptionFrequency: maybe(() =>
        findValueInEnum(formData.occurrence, CouponOccurenceEnum)
      ),
      startDate: changeTimeZone(
        formData.couponStartDate,
        formData.couponStartTime
      ),
      usageLimit: resolveCouponUsageLimitForUpdate(
        canManageCouponUsageLimitByDealType(
          userPermissions,
          formData.couponClassification || couponFullDetails?.dealType
        ),
        formData.usageLimit,
        couponFullDetails?.usageLimit
      ),
      usageTimerHour: formData.usageTimerHour,
      usageTimerMinute: formData.usageTimerMinute,
      usageTimerSecond: formData.usageTimerSecond,
      userType: maybe(() =>
        findValueInEnum(formData.userRestriction, CouponUserTypeEnum)
      )
    };

    if (currentCollection !== formData.couponCollection && currentCollection) {
      await removeCouponToCollection({
        variables: {
          coupons: { remove: [couponId] },
          instanceId: currentCollection
        }
      });
    }

    await updateCouponDraft({
      variables: {
        first: 100,
        id,
        input: couponDraftUpdateInput
      }
    });
  };

  const [updateCouponDraft] = useDraftCouponUpdate({
    onCompleted: handleCouponDraftUpdateSuccess
  });

  const [updateCoupon, updateCouponResult] = useCouponUpdate({});

  const couponSaveButtonBarState =
    updateCouponResult.status === "success"
      ? "default"
      : updateCouponResult.status;

  useEffect(() => {
    const url = window.location.href;
    if (!url.includes("coupon-ranking")) {
      createDeeplink({ variables: { id } });
    }
  }, []);

  useEffect(() => {
    const couponCategory =
      couponFullDetails?.dealType === "EVENTS"
        ? ("EVENTS" as CouponCollectionCategoryEnum)
        : (couponFullDetails?.category?.toString() as CouponCollectionCategoryEnum);
    const collectionCategories =
      currentCollectionDetails?.couponCollection?.categories;
    if (
      currentCollectionDetails !== undefined &&
      couponCategory !== undefined
    ) {
      if (
        !collectionCategories.includes(couponCategory) &&
        collectionCategories.length !== 0 &&
        couponCategory !== "EVENTS"
      ) {
        removeCouponToCollection({
          variables: {
            coupons: { remove: [couponId] },
            instanceId: currentCollection
          }
        });
      }
    }
  }, [couponFullDetails?.category, currentCollectionDetails]);

  return (
    <Container>
      <CouponFullDetails
        selectedCategory={selectedCategory}
        onAdditionalCopies={setAdditionalCopies}
        userPermissions={userPermissions}
        additionalCopies={additionalCopies}
        coupon={couponFullDetails}
        errors={updateCouponResult?.data?.couponUpdate?.errors || []}
        disabled={
          loading || createDeeplinkOpts.loading || updateCouponResult.loading
        }
        saveButtonBarState={couponSaveButtonBarState}
        onSubmit={handleSubmit}
        onBack={() => navigate(couponListPath)}
        accessImage={accessImageUpload}
        promoImage={promoImageUpload}
        onAccessImageUpload={setAccessImageUpload}
        onPromoImageUpload={setPromoImageUpload}
        malls={mallTags}
        fetchMoreMalls={{
          hasMore: maybe(() => searchMallOpts.data.search.pageInfo.hasNextPage),
          loading: searchMallOpts.loading,
          onFetchMore: loadMoreMalls
        }}
        fetchMall={searchMall}
        onGenerateDeeplink={handleGenerateDeeplink}
        onDeactivate={() => openModal("deactivate")}
        onActivate={() => handleCouponActivate(id)}
        onDraftUpdate={handleDraftUpdate}
        fetchMoreCollection={{
          hasMore: searchCollectionOpts.data?.search?.pageInfo?.hasNextPage,
          loading: searchCollectionOpts.loading,
          onFetchMore: loadMoreCollection
        }}
        setSelectedCategory={setSelectedCategory}
        couponCollections={(searchCollectionOpts.data?.search?.edges || []).map(
          edge => edge.node
        )}
        fetchCollections={searchCollection}
      />
      <ActionDialogVariant
        confirmButtonState={deactivateCouponResult.status}
        onClose={closeModal}
        onConfirm={() => handleCouponDeactivate(id)}
        warningTitle={intl.formatMessage({
          defaultMessage: "Deactivate Coupon",
          description: "dialog title"
        })}
        open={params.action === "deactivate"}
        confirmButtonLabel="Yes"
        buttonsAlignment="center"
        maxWidth="sm"
      >
        <FormattedMessage defaultMessage="Are you sure you want to deactivate the selected coupon(s)?" />
        <Typography className={classes.subContent}>
          <FormattedMessage defaultMessage="Coupon(s) selected will be deactivated." />
        </Typography>
      </ActionDialogVariant>
    </Container>
  );
};

export default CouponDetails;
